import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Loader2, ArrowLeft, Clock, Globe } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { detectTimezone, tzList } from "../lib/timezones";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Calendar } from "./ui/calendar";
import { es } from "date-fns/locale";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { dateLocale } from "../lib/meta";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function BookAppointment({ open, onOpenChange, psychologist, booking, initialTypeId }) {
  const { t, lang } = useLang();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const clientTz = user?.timezone || detectTimezone();
  const types = booking?.appointment_types || [];
  const [typeId, setTypeId] = useState(types.find((x) => x.is_default)?.id || types[0]?.id || "");
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState("pick"); // pick | auth | review | done
  const [confirming, setConfirming] = useState(false);
  const [chosenMod, setChosenMod] = useState("online");
  const [availDates, setAvailDates] = useState(null);
  const activeType = types.find((x) => x.id === typeId);
  const modality = activeType?.modality === "both" ? chosenMod : (activeType?.modality || "online");
  const fmtLocal = (utc, tz) => {
    try { return new Intl.DateTimeFormat(dateLocale(lang), { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz }).format(new Date(utc)); }
    catch { return ""; }
  };

  useEffect(() => {
    if (open) { setStep("pick"); setSelected(null); setDate(null); setSlots([]); setChosenMod("online"); setTypeId(initialTypeId || types.find((x) => x.is_default)?.id || types[0]?.id || ""); }
  }, [open]);

  useEffect(() => {
    if (step !== "pick") return;              // never refetch/clear once past slot selection
    if (!date || !typeId) { setSlots([]); return; }
    setLoadingSlots(true); setSelected(null);
    api.get(`/psychologists/${psychologist.id}/slots`, { params: { date: iso(date), type_id: typeId, modality, client_tz: clientTz } })
      .then((r) => setSlots(r.data)).catch(() => setSlots([])).finally(() => setLoadingSlots(false));
  }, [date, typeId, psychologist.id, modality, clientTz, step]);

  useEffect(() => {
    if (!open || !typeId) { setAvailDates(null); return; }
    setAvailDates(null);
    api.get(`/psychologists/${psychologist.id}/available-dates`, { params: { type_id: typeId, modality } })
      .then((r) => setAvailDates(new Set(r.data.dates))).catch(() => setAvailDates(null));
  }, [open, typeId, modality, psychologist.id]);

  const proceed = () => {
    if (!selected) return;
    if (!user) setStep("auth");
    else setStep("review");
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/appointments", {
        psychologist_id: psychologist.id, type_id: typeId, modality, start_utc: selected.start_utc, client_timezone: clientTz,
      });
      setStep("done");
      toast.success(t("booking.booked_ok"));
      return data;
    } catch (e) {
      const code = e.response?.status;
      if (code === 409) {
        toast.error(t("booking.slot_unavailable"));
        setStep("pick"); setSelected(null);
        if (date) api.get(`/psychologists/${psychologist.id}/slots`, { params: { date: iso(date), type_id: typeId, modality, client_tz: clientTz } }).then((r) => setSlots(r.data));
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally { setConfirming(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="book-appointment-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-terracotta" />{t("booking.book_cta")}</DialogTitle></DialogHeader>

        {step === "pick" && (
          <div className="space-y-4">
            <div>
              <Label>{t("booking.appt_type")}</Label>
              <Select value={typeId} onValueChange={setTypeId}>
                <SelectTrigger data-testid="book-type"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.label} · {tp.minutes}m · {t(`common.${tp.modality === "in_person" ? "in_person" : tp.modality}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {activeType?.modality === "both" && (
              <div>
                <Label>{t("booking.modality")}</Label>
                <Select value={chosenMod} onValueChange={setChosenMod}>
                  <SelectTrigger data-testid="book-modality"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">{t("common.online")}</SelectItem>
                    <SelectItem value="in_person">{t("common.in_person")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{t("booking.choose_date")}</Label>
              <div className="mt-1.5 flex justify-center rounded-xl border border-border bg-white p-2">
                <Calendar mode="single" selected={date} onSelect={setDate} locale={lang === "es" ? es : undefined} disabled={(d) => d < new Date(new Date().toDateString()) || !availDates || !availDates.has(iso(d))} data-testid="book-calendar" />
              </div>
              {availDates && <p className="mt-1.5 text-xs text-muted-foreground">{t("booking.no_slots_hint")}</p>}
            </div>
            {date && (
              <div>
                <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Globe className="h-3.5 w-3.5" />{t("booking.your_local_time")}: {clientTz}</p>
                {loadingSlots ? <div className="py-4 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
                  : slots.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground" data-testid="no-slots">{t("booking.no_times_day")}</p>
                  : <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="slot-grid">
                      {slots.map((s) => (
                        <button key={s.start_utc} onClick={() => setSelected(s)} data-testid={`slot-${s.start_utc}`}
                          className={`rounded-lg border px-2 py-2 text-sm transition-colors ${selected?.start_utc === s.start_utc ? "border-terracotta bg-terracotta text-white" : "border-border hover:border-terracotta/50"}`}>
                          {s.client_local}
                        </button>
                      ))}
                    </div>}
              </div>
            )}
            <Button disabled={!selected} onClick={proceed} className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="book-continue">{t("booking.continue")}</Button>
          </div>
        )}

        {step === "auth" && (
          <AuthStep onDone={async () => { await refresh(); setStep("review"); }} lang={lang} t={t} />
        )}

        {step === "review" && selected && (
          <div className="space-y-4" data-testid="book-review">
            <div className="rounded-xl border border-border bg-cream/50 p-4 text-sm">
              <p className="font-display text-lg font-semibold text-slateink">{psychologist.full_name}</p>
              <p className="mt-2 flex items-center gap-1.5"><Clock className="h-4 w-4 text-terracotta" /><b>{fmtLocal(selected.start_utc, clientTz)}</b> · {new Date(selected.start_utc).toLocaleDateString(dateLocale(lang), { weekday: "long", day: "numeric", month: "long", timeZone: clientTz })}</p>
              <p className="mt-1 text-muted-foreground">{t("booking.your_local_time")}: {clientTz}</p>
              <p className="text-muted-foreground">{t("booking.professional_time")}: {selected.psych_local} ({booking?.timezone})</p>
              <p className="mt-1">{t("booking.modality")}: {t(`common.${modality === "in_person" ? "in_person" : modality}`)} · {activeType?.minutes}m</p>
            </div>
            {booking?.cancellation_policy && <p className="rounded-lg bg-cream px-3 py-2 text-xs text-muted-foreground">{booking.cancellation_policy}</p>}
            <Button onClick={confirm} disabled={confirming} className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="book-confirm">
              {confirming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("booking.confirm_booking")}
            </Button>
          </div>
        )}

        {step === "review" && !selected && (
          <div className="py-6 text-center" data-testid="book-review-empty">
            <p className="text-sm text-muted-foreground">{t("booking.pick_time_again")}</p>
            <Button variant="outline" className="mt-4 rounded-full" onClick={() => setStep("pick")}>{t("booking.back")}</Button>
          </div>
        )}
        {step === "done" && (
          <div className="space-y-4 py-4 text-center" data-testid="book-done">
            <CalendarClock className="mx-auto h-10 w-10 text-terracotta" />
            <p className="font-medium text-slateink">{t("booking.appt_confirmed")}</p>
            <p className="text-sm text-muted-foreground">{t("booking.confirmation_email_sent")}</p>
            <Button onClick={() => navigate("/mis-citas")} className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="book-goto-appts">{t("booking.my_appts")}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AuthStep({ onDone, lang, t }) {
  const [mode, setMode] = useState("register");
  const [f, setF] = useState({ name: "", email: "", password: "", timezone: detectTimezone(), phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (mode === "register") {
        await api.post("/auth/register", { name: f.name, email: f.email, password: f.password, role: "client", accept_terms: true, accept_privacy: true, lang, timezone: f.timezone, phone: f.phone });
      } else {
        await api.post("/auth/login", { email: f.email, password: f.password, lang });
      }
      await onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="book-auth">
      <div className="flex gap-1 rounded-full border border-border p-1 text-sm">
        {[["register", t("booking.create_account")], ["login", t("booking.sign_in")]].map(([m, l]) => (
          <button type="button" key={m} onClick={() => setMode(m)} className={`flex-1 rounded-full px-3 py-1.5 ${mode === m ? "bg-slateink text-white" : "text-slateink/70"}`} data-testid={`auth-mode-${m}`}>{l}</button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{t("booking.account_needed")}</p>
      {mode === "register" && <div><Label>{t("booking.full_name")}</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} required data-testid="auth-name" /></div>}
      <div><Label>{t("booking.email")}</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required data-testid="auth-email" /></div>
      <div><Label>{t("booking.password")}</Label><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} required data-testid="auth-password" /></div>
      {mode === "register" && (
        <>
          <div><Label>{t("booking.phone_opt")}</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="auth-phone" /></div>
          <div>
            <Label>{t("booking.your_timezone")}</Label>
            <Select value={f.timezone} onValueChange={(v) => set("timezone", v)}>
              <SelectTrigger data-testid="auth-timezone"><SelectValue /></SelectTrigger>
              <SelectContent>{tzList().map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">{t("booking.tz_autodetect")}</p>
          </div>
        </>
      )}
      <Button type="submit" disabled={busy} className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="auth-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "register" ? t("booking.create_continue") : t("booking.sign_in"))}</Button>
    </form>
  );
}
